import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardBody, Button, Spinner, Alert, Input, Badge, Switch, Modal } from '@components/ui';
import { PlusIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useInvoices } from '../../invoices/hooks/useInvoices';
import { useSelectedCompany } from '../../company/hooks/useSelectedCompany';
import { useInvoiceFilterStore, filterErrorInvoices } from '@/stores/invoiceFilterStore';

// Mapeo de tipos de documento
const documentTypeLabels: Record<string, string> = {
  '01': 'Factura Operación Interna',
  '02': 'Factura Importación',
  '03': 'Factura Exportación',
  '04': 'Nota de Crédito',
  '05': 'Nota de Débito',
  '06': 'NC Genérica',
  '07': 'ND Genérica',
  '08': 'Factura Zona Franca',
  '09': 'Reembolso',
  '10': 'Factura Op. Extranjera',
};

// Mapeo de colores segun EInvoiceStatus (equivalente a los label-* de Bootstrap)
const getStatusVariant = (eInvoiceStatus: number): 'success' | 'danger' | 'warning' | 'default' => {
  switch (eInvoiceStatus) {
    case 0: return 'success';    // Procesado
    case 1: return 'danger';     // Error
    case 2: return 'warning';    // Autorizado uso de FE
    case 3: return 'warning';    // Procesando
    case 4: return 'warning';    // Pendiente
    case 5: return 'default';    // Anulada
    default: return 'default';
  }
};

// Mapeo de variante de Badge a color de texto para el icono de advertencia
const getIconColorClass = (variant: string): string => {
  switch (variant) {
    case 'info': return 'text-primary-600';
    case 'danger': return 'text-danger-600';
    case 'warning': return 'text-warning-600';
    default: return 'text-secondary-600';
  }
};

function StatusBadgeWithModal({
  status,
  eInvoiceStatus,
  responseCode,
  resolution,
  forceWaitOnEndpoints,
}: {
  status: string;
  eInvoiceStatus: number;
  responseCode?: string;
  resolution?: string;
  forceWaitOnEndpoints?: string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const variant = getStatusVariant(eInvoiceStatus);
  const tooltipText = [responseCode, resolution].filter(Boolean).join(' - ');
  const showWarningIcon = forceWaitOnEndpoints === 'true';
  const iconColor = getIconColorClass(variant);

  return (
    <>
      <span className="inline-flex items-center gap-1" title={tooltipText || undefined}>
        {showWarningIcon && (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className={`${iconColor} hover:opacity-70 transition-opacity`}
            title="Ver detalle del estado"
          >
            <ExclamationCircleIcon className="w-4 h-4" />
          </button>
        )}
        <button type="button" onClick={() => setIsModalOpen(true)}>
          <Badge variant={variant} size="sm">
            {status || 'Desconocido'}
          </Badge>
        </button>
      </span>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Detalle del estado"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant={variant} size="md">
              {status || 'Desconocido'}
            </Badge>
          </div>
          {responseCode && (
            <div className="rounded-lg bg-secondary-50 dark:bg-secondary-700 p-3">
              <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
                Código de respuesta
              </p>
              <p className="mt-1 text-sm text-secondary-900 dark:text-white">
                {responseCode}
              </p>
            </div>
          )}
          {resolution && (
            <div className="rounded-lg bg-secondary-50 dark:bg-secondary-700 p-3">
              <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
                Resolución
              </p>
              <p className="mt-1 text-sm text-secondary-900 dark:text-white">
                {resolution}
              </p>
            </div>
          )}
          {!responseCode && !resolution && (
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              No hay información adicional disponible.
            </p>
          )}
        </div>
        <div className="mt-6 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setIsModalOpen(false)}>
            Cerrar
          </Button>
        </div>
      </Modal>
    </>
  );
}


export function MainPage() {
  // Calcular el primer día del mes actual
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  };

  // Calcular el día de hoy
  const getToday = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Calcular el día de mañana (hoy + 1)
  const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [initDate, setInitDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());

  // Global filter state (persists across views)
  const { hideErrorInvoices, setHideErrorInvoices } = useInvoiceFilterStore();

  // Estados para los filtros aplicados (los que se envían al API)
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedInitDate, setAppliedInitDate] = useState(getFirstDayOfMonth());
  const [appliedEndDate, setAppliedEndDate] = useState(getTomorrow()); // Enviar mañana al API

  // Get selected company
  const { data: selectedCompanyData } = useSelectedCompany();

  // Extract companyId to use in the query key
  const companyId = (selectedCompanyData as any)?.company?.id || selectedCompanyData?.id;

  const { data, isLoading, error, refetch } = useInvoices({
    page,
    pageSize: 10,
    search: appliedSearch,
    initDate: appliedInitDate,
    endDate: appliedEndDate,
    companyId, // Include companyId so React Query creates a new cache entry per company
  });

  // Debug: log selected company and invoice data
  console.log('🏢 MainPage - Selected company:', {
    id: (selectedCompanyData as any)?.company?.id || selectedCompanyData?.id,
    name: (selectedCompanyData as any)?.company?.name || (selectedCompanyData as any)?.name,
    code: (selectedCompanyData as any)?.company?.code || (selectedCompanyData as any)?.code
  });
  console.log('📋 MainPage - Invoice data:', {
    count: data?.invoices?.length,
    total: data?.total,
    firstInvoice: data?.invoices?.[0]?.customerName,
    firstInvoiceId: data?.invoices?.[0]?.id
  });

  // Filter invoices to hide those with errors if toggle is on (uses global store)
  const filteredInvoices = filterErrorInvoices(data?.invoices || [], hideErrorInvoices);

  const handleSearch = () => {
    // Calcular endDate + 1 día para enviar al API
    const endDatePlusOne = new Date(endDate);
    endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
    const endDateForAPI = endDatePlusOne.toISOString().split('T')[0];

    setAppliedSearch(searchTerm);
    setAppliedInitDate(initDate);
    setAppliedEndDate(endDateForAPI); // Enviar fecha + 1 día al API
    setPage(1); // Reset a la primera página al buscar
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    const err = error as any;
    const status = err?.response?.status;
    const message = err?.response?.data?.message || err?.message || 'Error desconocido';
    return (
      <Alert variant="danger" title="Error al cargar facturas">
        <p>
          {status ? `[${status}] ` : ''}
          {message}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">
          Reintentar
        </Button>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="bg-secondary-50 dark:bg-secondary-800">
        <CardBody>
          <div className="flex items-center justify-end mb-4">
            <Link to="/invoices/new">
              <Button variant="primary" leftIcon={<PlusIcon className="w-5 h-5" />}>
                Emitir Factura
              </Button>
            </Link>
          </div>

          <div className="text-center py-6">
            <div className="flex items-center justify-center gap-4 mb-4">
              <DocumentTextIcon className="h-12 w-12 text-secondary-900 dark:text-white" />
              <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
                Facturas emitidas
              </h1>
            </div>
            <p className="text-secondary-600 dark:text-secondary-400 mb-6">
              Desde aquí puedes registrar, consultar, ver y editar las facturas de tu compañía.
            </p>

            {/* Search Filters */}
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  type="text"
                  label="Buscar"
                  placeholder="Número de factura o cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
                <Input
                  type="date"
                  label="Fecha Inicio"
                  value={initDate}
                  onChange={(e) => setInitDate(e.target.value)}
                />
                <Input
                  type="date"
                  label="Fecha Fin"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:justify-center sm:gap-6">
                <Button
                  variant="primary"
                  onClick={handleSearch}
                  className="px-8"
                  disabled={isLoading}
                >
                  {isLoading ? 'Buscando...' : 'Buscar'}
                </Button>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={hideErrorInvoices}
                    onChange={setHideErrorInvoices}
                    label="Ocultar facturas con error"
                    size="sm"
                  />
                  {hideErrorInvoices && data?.invoices && (
                    <span className="text-xs text-secondary-500 dark:text-secondary-400">
                      ({data.invoices.length - filteredInvoices.length} ocultas)
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardBody className="p-0">
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-secondary-400" />
              <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
                No hay facturas
              </h3>
              <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                Comienza creando tu primera factura
              </p>
              <div className="mt-6">
                <Link to="/invoices/new">
                  <Button variant="primary">
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Nueva Factura
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                <thead className="bg-secondary-50 dark:bg-secondary-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      # Documento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      RUC/Cédula
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
                  {filteredInvoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900 dark:text-white">
                        {invoice.documentNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900 dark:text-white">
                        <Badge variant="default" size="sm">
                          {invoice.documentType ? (documentTypeLabels[invoice.documentType] || invoice.documentType) : '-'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900 dark:text-white">
                        {invoice.customerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                        {invoice.customerNumberId || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-secondary-900 dark:text-white">
                        ${(invoice.amountWithTax ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900 dark:text-white">
                        {invoice.dateIssue && format(new Date(invoice.dateIssue), 'dd/MM/yyyy HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadgeWithModal
                          status={invoice.status ?? ''}
                          eInvoiceStatus={invoice.eInvoiceStatus ?? 0}
                          responseCode={invoice.responseCode}
                          resolution={invoice.resolution}
                          forceWaitOnEndpoints={invoice.forceWaitOnEndpoints}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/pages/invoice/${invoice.id}`}
                          state={{ from: '/home/main' }}
                          className="text-primary-600 hover:text-primary-900 dark:hover:text-primary-400"
                        >
                          Ver detalles
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Paginación */}
      {data && filteredInvoices.length > 0 && (
        <Card>
          <CardBody className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm text-secondary-700 dark:text-secondary-300">
                  Mostrando {(page - 1) * data.pageSize + 1} a{' '}
                  {Math.min(page * data.pageSize, data.total)} de {data.total} facturas
                </p>
                <div className="text-xs text-secondary-500 dark:text-secondary-400">
                  Página {page} de {Math.ceil(data.total / data.pageSize)}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page * data.pageSize >= data.total}
                  onClick={() => setPage(page + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
